import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Lineicons } from '@lineiconshq/react-lineicons';
import { XmarkStroke } from '@lineiconshq/free-icons';
import React, { useState } from 'react';
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from '@/components/ui/dialog';
import { DieuKienThanhToan, DieuKienThanhToanInput, KhachHang } from '@/types/schemas';
import { AritoFormV2, BottomActionBarV2, AritoIcon } from '@/components/custom/erp';
import { useInvoiceCollectionRows, usePaymentDay, useWeek } from './hooks';
import { BasicInfo, GeneralTab, InvoiceCollectionDayTab } from './tabs';
import { initialValues, searchSchema } from '../../schema';
import { useDialogStack } from '@/hooks/use-dialog-stack';
import { toast } from '@/hooks/use-toast';

interface InitialSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: DieuKienThanhToanInput) => void;
  formMode: 'add' | 'edit' | 'view';
  initialData?: DieuKienThanhToan;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
  dieuKienThanhToans?: DieuKienThanhToan[];
}

export const FormDialog: React.FC<InitialSearchDialogProps> = ({
  open,
  onClose,
  onSubmit,
  formMode,
  initialData,
  onAdd,
  onEdit,
  onDelete,
  onCopy,
  dieuKienThanhToans = []
}) => {
  const { contentZIndex, overlayZIndex } = useDialogStack(open);
  const {
    rows: invoiceCollectionRows,
    selectedRowUuid,
    handleRowClick,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    handleCellValueChange
  } = useInvoiceCollectionRows(initialData?.ngay_gom_chi_tiet || []);

  const { selectedPaymentDay, setSelectedPaymentDay } = usePaymentDay(
    initialData && typeof initialData === 'object' && 'ngay_tt_thang' in initialData ? initialData.ngay_tt_thang : null
  );
  const { selectedWeek, setSelectedWeek } = useWeek(
    initialData && typeof initialData === 'object' && 'tuan_tt_thu' in initialData ? initialData.tuan_tt_thu : null
  );
  const [khachHang, setKhachHang] = useState<KhachHang | null>(
    initialData && typeof initialData === 'object' && 'ma_kh_data' in initialData ? initialData.ma_kh_data : null
  );

  const handleSubmit = async (formData: BanHangPayload) => {
    try {
      if (!khachHang?.uuid) {
        throw new Error('Vui lòng chọn khách hàng');
      }
      if (formMode === 'add') {
        const existingDieuKiens = Array.isArray(dieuKienThanhToans) ? dieuKienThanhToans : [];
        const isDuplicate = existingDieuKiens.some(
          (dk: DieuKienThanhToan) => dk.ma_dk === formData.ma_dk && (!initialData || dk.uuid !== initialData.uuid)
        );
        if (isDuplicate) {
          toast({
            description: 'Vui lòng nhập mã điều kiện khác',
            variant: 'destructive'
          });
          return;
        }
      } else if (formMode === 'edit' && initialData) {
        const existingDieuKiens = Array.isArray(dieuKienThanhToans) ? dieuKienThanhToans : [];
        const isDuplicate = existingDieuKiens.some(
          (dk: DieuKienThanhToan) => dk.ma_dk === formData.ma_dk && dk.uuid !== initialData.uuid
        );

        if (isDuplicate) {
          toast({
            description: 'Vui lòng nhập mã điều kiện khác',
            variant: 'destructive'
          });
          return;
        }
      }

      const submitData: DieuKienThanhToanInput = {
        ...formData,
        ma_kh: khachHang?.uuid || '',
        ngay_tt_thang: selectedPaymentDay?.ma || '',
        tuan_tt_thu: selectedWeek?.value || '',
        ngay_gom_chi_tiet: invoiceCollectionRows.map((row, index) => ({
          ma_dk: formData.ma_dk,
          line: index + 1,
          ngay_gom_hd1: row.ngay_gom_hd1,
          ngay_gom_hd2: row.ngay_gom_hd2
        }))
      };

      onSubmit(submitData);
      onClose();
    } catch (err: unknown) {
      const error = err as BanHangRecord;
      toast({
        description: error.message || 'Lỗi khi cập nhật điều kiện thanh toán',
        variant: 'destructive'
      });
    }
  };

  return (
    <Dialog modal={true} open={open} onOpenChange={onClose}>
      <DialogPortal>
        <DialogOverlay className='fixed inset-0 bg-black/50' style={{ zIndex: overlayZIndex }} />
        <div className='fixed inset-0 flex items-center justify-center p-4' style={{ zIndex: contentZIndex }}>
          <DialogPrimitive.Content
            onOpenAutoFocus={event => event.preventDefault()}
            onCloseAutoFocus={event => event.preventDefault()}
            onInteractOutside={event => event.preventDefault()}
            onPointerDownOutside={event => event.preventDefault()}
            onFocusOutside={event => event.preventDefault()}
            className='flex max-h-[85vh] w-[820px] flex-col overflow-hidden border border-[#cfd8e3] bg-white shadow-lg outline-none'
          >
            <div className='flex h-11 min-h-11 items-center justify-between border-b border-[#d1d5db] bg-[#f2f7fc] pl-3.5 pr-4'>
              <DialogTitle className='flex items-center text-[16px] font-bold text-black'>
                <AritoIcon icon={12} className='mx-2' />
                <span>{formMode === 'edit' ? 'Sửa điều kiện thanh toán' : 'Cập nhật điều kiện thanh toán'}</span>
              </DialogTitle>
              <button type='button' onClick={onClose} className='inline-flex size-8 items-center justify-center'>
                <Lineicons aria-hidden='true' icon={XmarkStroke} className='size-[18px]' />
              </button>
            </div>
            <AritoFormV2
              mode={formMode}
              hasAritoActionBar={false}
              schema={searchSchema}
              initialData={
                formMode === 'add' && initialData
                  ? {
                      ...(initialData as BanHangRecord),
                      uuid: undefined,
                      ma_dk: ''
                    }
                  : (initialData as BanHangRecord) || initialValues
              }
              onSubmit={handleSubmit}
              className='max-h-[calc(100vh-150px)] w-full overflow-y-auto'
              headerFields={
                <>
                  <BasicInfo khachHang={khachHang} setKhachHang={setKhachHang} formMode={formMode} />
                </>
              }
              tabs={[
                {
                  id: 'ttc',
                  label: 'Thông tin chung',
                  component: (
                    <GeneralTab
                      selectedPaymentDay={selectedPaymentDay}
                      setSelectedPaymentDay={setSelectedPaymentDay}
                      selectedWeek={selectedWeek}
                      setSelectedWeek={setSelectedWeek}
                      formMode={formMode}
                    />
                  )
                },
                {
                  id: 'nghd',
                  label: 'Ngày gom hóa đơn',
                  component: (
                    <InvoiceCollectionDayTab
                      mode={formMode}
                      rows={invoiceCollectionRows}
                      selectedRowUuid={selectedRowUuid}
                      onRowClick={handleRowClick}
                      onAddRow={handleAddRow}
                      onDeleteRow={handleDeleteRow}
                      onCopyRow={handleCopyRow}
                      onPasteRow={handlePasteRow}
                      onMoveRow={handleMoveRow}
                      onCellValueChange={handleCellValueChange}
                    />
                  )
                }
              ]}
              bottomBar={
                <BottomActionBarV2
                  mode={formMode}
                  onAdd={onAdd}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onCopy={onCopy}
                  onSubmit={() => {}}
                  onClose={onClose}
                />
              }
            />
          </DialogPrimitive.Content>
        </div>
      </DialogPortal>
    </Dialog>
  );
};

export default FormDialog;
